import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AppLocalStorage } from "app/app-storage-manager/local-storage";
import { environment } from "environments/environment";
import { BehaviorSubject, of } from "rxjs";
import { Observable } from "rxjs-compat";
import { map, retry, tap } from "rxjs/operators";
import { AuthHeaderService } from "./auth-header.service";

@Injectable({
  providedIn: "root",
})
export class MiscService {
  constructor(
    private appStorage: AppLocalStorage,
    private http: HttpClient,
    private authHeaderService: AuthHeaderService,
  ) {}

  public getMerchantDetails = () => this.appStorage.getFromStorage("user");

  user = undefined;
  paymentCustomer = undefined;
  userSubject = new BehaviorSubject(this.user);
  paymentCustomerSubject = new BehaviorSubject(this.paymentCustomer);

  updateUser = (update) => {
    this.user = update;
    this.userSubject.next(this.user);
    this.appStorage.storeToStorage("user", this.user);
  };

  updatePaymentCustomer = (customer) => {
    this.paymentCustomer = customer;
    this.paymentCustomerSubject.next(this.paymentCustomer);
    this.appStorage.storeToStorage("logged-in-customer", this.paymentCustomer);
  };

  genRandomString = (length) =>
    Math.round(Math.pow(36, length + 1) - Math.random() * Math.pow(36, length))
      .toString(36)
      .slice(1);

  // numberWithCommas = (number) => {
  //     if (!number) { return '0'; }
  //     const parts = number.toString().split('.');
  //     parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  //     return  parseFloat(parts.join('.')).toFixed(2);
  //   }
  numberWithCommas = (x) => {
    if (!x) {
      return "0";
    }
    var parts = x.toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    parts = parts.join(".");
    if (parts.includes(".")) return parts;
    else return parts + ".00";
  };

  formatDateAndTime = (date: string | null) => {
    if (!date) {
      return "";
    }
    const dateToFormat = new Date(date);
    return (
      dateToFormat.toDateString() + " " + dateToFormat.toLocaleTimeString()
    );
  };
  formatDate = (date: string | null) => {
    if (!date) {
      return "";
    }
    const dateToFormat = new Date(date);
    return dateToFormat.toDateString();
  };
  formatTime = (date: string | null) => {
    if (!date) {
      return "";
    }
    const dateToFormat = new Date(date);
    return dateToFormat.toLocaleTimeString();
  };

  formatTo2DecimalPlaces = (number: any) => {
    if (!number) {
      return;
    }
    number = number.toString();
    if (number.includes(".")) {
      number = parseFloat(number);
      return parseFloat(number.toFixed(2)).toString();
    } else {
      return number + ".00";
    }
  };

  updateProfilePic(file: any, merchant_id: string): Observable<any> {
    let form = new FormData();
    form.append("file", file);
    return this.http
      .put(`${environment.API_URL}/merchant/upload-logo/${merchant_id}`, form)
      .pipe(retry(3));
  }

  fetchImageUrl(file: any): Observable<any> {
    let form = new FormData();
    form.append("image", file);
    return this.http
      .post(`${environment.API_URL2}/transactions-service/bucket/upload`, form)
      .pipe(retry(3));
  }

  bulkRecipients(payload: any): Observable<any> {
    const form = new FormData();
    form.append("file", payload.file);
    form.append("country_code", payload.country_code);
    form.append("category_id", payload.category_id);
    form.append("scheme_code", payload.scheme_code);

    // Create payload object for auth header (without the file)
    const authPayload = {
      country_code: payload.country_code,
      category_id: payload.category_id,
      scheme_code: payload.scheme_code,
      // Add any other required fields for auth
    };

    const xAuthHeader = this.authHeaderService.generateAuthHeader(authPayload);

    const headers = new HttpHeaders()
      .set("X-Auth-Signature", xAuthHeader)
      .set("X-APP-ID", `${environment.X_APP_ID}`)
      .set("X-ISSUER-ID", `${environment.X_ISSUER_ID}`);
    // Don't set Content-Type - let browser set it automatically for FormData

    return this.http
      .post(`${environment.API_URL3}/transactions/recipients/bulk`, form, {
        headers,
      })
      .pipe(retry(3));
  }

  getBulkJobStatus(jobId: string): Observable<any> {
    const xAuthHeader = this.authHeaderService.generateAuthHeader({ jobId });
    const headers = new HttpHeaders()
      .set("X-Auth-Signature", xAuthHeader)
      .set("X-APP-ID", `${environment.X_APP_ID}`)
      .set("X-ISSUER-ID", `${environment.X_ISSUER_ID}`);
    return this.http.get(
      `${environment.API_URL3}/transactions/recipients/bulk-status/${jobId}`,
      { headers },
    );
  }

  getJobs(params: any): Observable<any> {
    let queryParams = new HttpParams()
      .append("page", params.page || 1)
      .append("size", params.size || 10)
      .append("status", params.status || "")
      .append("jobType", params.jobType || "")
      .append("category_id", params.category_id || "")
      .append("startDate", params.startDate || "")
      .append("endDate", params.endDate || "");

    // ✅ Add authentication headers (same pattern as bulk upload)
    const xAuthHeader = this.authHeaderService.generateAuthHeader(params);
    const headers = new HttpHeaders()
      .set("X-Auth-Signature", xAuthHeader)
      .set("X-APP-ID", `${environment.X_APP_ID}`)
      .set("X-ISSUER-ID", `${environment.X_ISSUER_ID}`);

    return this.http
      .get(`${environment.API_URL3}/transactions/recipients/bulk/jobs`, {
        params: queryParams,
        headers: headers, // ✅ Include headers
      })
      .pipe(retry(3));
  }

  transformBranchName(branchName: string): string {
    return branchName?.replace(/Winners Chapel International/i, "WCI") || "";
  }

  fetchUtilityTypes(): Observable<any> {
    return this.http
      .get(`${environment.API_URL2}/transactions-service/utility-type`)
      .pipe(retry(3));
  }

  getColorName(hex: string): string {
    if (!hex) return "";

    // Remove '#' if present
    hex = hex.replace("#", "");

    // Convert hex to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Very basic matching
    if (r > 200 && g < 100 && b < 100) return "Red";
    if (r < 100 && g > 200 && b < 100) return "Green";
    if (r < 100 && g < 100 && b > 200) return "Blue";
    if (r > 200 && g > 200 && b < 100) return "Yellow";
    if (r > 200 && g < 100 && b > 200) return "Pink";
    if (r < 100 && g > 200 && b > 200) return "Cyan";
    if (r > 200 && g > 200 && b > 200) return "White";
    if (r < 50 && g < 50 && b < 50) return "Black";

    return "Unknown Color"; // fallback
  }
  //    function formatTo2DecimalPlaces(number){
  //         if (!number) { return; }
  //         parseFloat(number).toFixed(2)
  //     }

  validateXcelAccount = (walletNumber, schemeCode): Observable<any> => {
    return this.http
      .get(
        `${environment.API_URL3}/merchant/accounts/validate/${walletNumber}/${schemeCode}`,
      )
      .pipe(retry(3));
  };
  // validateXcelAccount = (walletNumber, schemeCode): Observable<any> => {
  //     return this.http.get(`${environment.API_URL}/source/details/${walletNumber}/${schemeCode}`).pipe(retry(3))
  //   }

  formatCountryCode = (countryCode) =>
    countryCode.length == 1
      ? "00" + countryCode
      : countryCode.length == 2
        ? "0" + countryCode
        : countryCode;

  getSettlementAccounts = () =>
    this.http.get(
      `${environment.API_URL}/merchant/banks/${this.getMerchantDetails().merchant_id}`,
    );

  createSettlementAccount = (body) =>
    this.http.post(`${environment.API_URL}/merchant/add/bank`, body);

  getMerchantWallets = () =>
    this.http.get(
      `${environment.API_URL}/merchant/wallets/${this.getMerchantDetails().merchant_id}`,
    );

  createMerchantWallet = (body) =>
    this.http.post(`${environment.API_URL}/merchant/wallet`, body);

  createMerchantWalletUK1 = (body) =>
    this.http.post(`${environment.API_URL}/psp/account`, body);

  createMerchantWalletUK2 = (body) =>
    this.http.post(`${environment.API_URL}/merchants/modulr/account`, body);

  // validateAccount = (currencyCode, bankCode, accountNumber) => this.http.get(`${environment.API_URL}/psp/validate/account/${currencyCode}/${bankCode}/${accountNumber}`)

    validateAccount = (bankCode, accountNumber, countryCode, providerCode?: string): Observable<any> => {
        const xAuthHeader = this.authHeaderService.generateAuthHeader({});
        const headers = new HttpHeaders()
            .set('X-Auth-Signature', xAuthHeader)
            .set('X-APP-ID', `${environment.X_APP_ID}`)
            .set('X-ISSUER-ID', `${environment.X_ISSUER_ID}`);
        const params: any = {};
        if (providerCode) params['to_provider_code'] = providerCode;
        return this.http.get(`${environment.API_URL3}/merchant/accounts/validate/${countryCode}/${accountNumber}/${bankCode}/external`, { headers, params })
    }

  getBanks = () => {
    if (this.getMerchantDetails().currency.iso_code_2 === "GB") {
      return of([]);
    } else {
      return this.http.get(
        `${environment.API_URL}/banks/${this.getMerchantDetails().country_code}`,
      );
    }
  };

  getCountries = () =>
    this.http.get(`${environment.API_URL}/business/countries`);

  validateSortCode = (countryCode, sort_code) => {
    const xAuthHeader = this.authHeaderService.generateAuthHeader({});
    const headers = new HttpHeaders()
      .set("X-Auth-Signature", xAuthHeader)
      .set("X-APP-ID", `${environment.X_APP_ID}`)
      .set("X-ISSUER-ID", `${environment.X_ISSUER_ID}`);
    return this.http.get(
      `${environment.API_URL3}/merchant/accounts/validate-bank-code/${countryCode}/${sort_code}`,
      { headers },
    );
  };

  getCountryDetails = (iso_2: string): Observable<any> =>
    this.http.get(`${environment.API_URL2}/administrator/countries/${iso_2}`);

  performBalanceInquiry = (accountIdentifier) => {
    if (this.getMerchantDetails().currency.iso_code_2 === "GB") {
      return this.http.get(
        `${environment.API_URL}/psp/account/balance/${accountIdentifier}`,
      );
    } else {
      return this.http.get(
        `${environment.API_URL}/source/wallet/balance/${accountIdentifier}`,
      );
    }
  };

  requestTransactionOTP = (accountIdentifier) => {
    const schemeCode = this.getMerchantDetails().country_code + "004";
    return this.http.get(
      `${environment.API_URL}/source/otp/${accountIdentifier}/${schemeCode}?group=true`,
    );
  };
  requestTransactionOTP2 = (payload) => {
    return this.http.post(
      `${environment.ESA_URL}/otp/generate/payment-authorization`,
      payload,
    );
  };
  verifyOTP = (payload) => {
    return this.http.post(
      `${environment.ESA_URL}/otp/verify/payment-authorization`,
      payload,
    );
  };

  fetchSubMerchants(merchantId: string): Observable<any> {
    return this.http
      .get(
        `${environment.TRANSACTION_URL}/merchant/${merchantId}/sub-merchants`,
      )
      .pipe(retry(3));
  }

  applyForUSSD(merchantId: string): Observable<any> {
    const body = {
      merchant_id: merchantId,
    };

    const xAuthHeader = this.authHeaderService.generateAuthHeader(body);
    const headers = new HttpHeaders()
      .set("x-auth-signature", xAuthHeader)
      .set("x-app-id", `${environment.X_APP_ID}`)
      .set("x-issuer-id", `${environment.X_ISSUER_ID}`);

    return this.http.post(
      `${environment.API_URL3}/merchants/ussd-application`,
      body,
      { headers },
    );
  }

  getUSSDApplication(merchantId: string): Observable<any> {
    const xAuthHeader = this.authHeaderService.generateAuthHeader({});
    const headers = new HttpHeaders()
      .set("x-auth-signature", xAuthHeader)
      .set("x-app-id", `${environment.X_APP_ID}`)
      .set("x-issuer-id", `${environment.X_ISSUER_ID}`);

    return this.http.get(
      `${environment.API_URL3}/merchants/ussd-application/${merchantId}`,
      { headers },
    );
  }
}
